import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { resolveHomeFor } from "@/lib/roleHome";
import { base44 } from "@/api/base44Client";
import { cn } from "@/lib/utils";
import MatchbaseLogo from "@/components/brand/MatchbaseLogo";

// One header for every public page. `overlay` = transparent, on top of the home page's stadium hero.
// Who sees "Log in", "My Team Zone" or "My account" is unchanged.
const NAV = [
  { to: "/tournaments", label: "Find a tournament" },
  { to: "/live", label: "Live scores" },
  { to: "/organisers", label: "For organisers" },
  { to: "/about", label: "About" },
];
const SHADOW = "shadow-[0_8px_24px_-10px_rgb(0_0_0/0.45)]";

const Logo = ({ onDark = true }) => (
  <Link to="/" aria-label="Matchbase, home" className="flex shrink-0 items-center">
    <MatchbaseLogo tone={onDark ? "dark" : "light"} height={30} />
  </Link>
);

export default function SiteHeader({ overlay = false }) {
  const { user, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [home, setHome] = useState("/team");
  const location = useLocation();
  const firstLink = useRef(null);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (isAuthenticated && user) resolveHomeFor(base44, user).then(setHome);
  }, [isAuthenticated, user]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    firstLink.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const pill = "inline-flex h-10 items-center justify-center rounded-full px-5 text-sm font-medium transition-colors";
  const dark = cn(pill, "border border-white/15 bg-[#0B1630] text-white hover:bg-[#16233F]", SHADOW);
  const light = cn(pill, "bg-white text-[#0B1630] hover:bg-white/90", SHADOW);
  const isOn = (to) => location.pathname.startsWith(to);

  return (
    <header className={cn("z-40", overlay ? "absolute inset-x-0 top-0" : "sticky top-0 border-b border-white/[0.08] bg-[#0B1630]")}>
      <div className="mx-auto flex h-[72px] max-w-[1200px] items-center justify-between gap-4 px-4 lg:px-6">
        <Logo />
        <nav aria-label="Main" className={cn("hidden items-center gap-0.5 rounded-full bg-white px-1.5 py-1.5 md:flex", SHADOW)}>
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} aria-current={isOn(n.to) ? "page" : undefined}
              className={cn("relative rounded-full px-4 py-1.5 text-sm font-medium transition-colors", isOn(n.to) ? "text-[#0B1630]" : "text-[#0B1630]/60 hover:text-[#0B1630]")}>
              {n.label}
              {isOn(n.to) && <span aria-hidden className="absolute bottom-0.5 left-1/2 flex -translate-x-1/2 gap-[3px]">{[0, 1, 2].map((i) => <span key={i} className="h-[3px] w-[3px] rounded-full bg-tf-blue" />)}</span>}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          {isAuthenticated && home === "/team" && <Link to="/team" className={cn(light, "hidden md:inline-flex")}>My Team Zone</Link>}
          {isAuthenticated
            ? <Link to="/account" className={cn(dark, "hidden md:inline-flex")}>My account</Link>
            : <Link to="/login" className={cn(dark, "hidden md:inline-flex")}>Log in</Link>}
          <button type="button" onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open}
            className={cn("flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#0B1630] md:hidden", SHADOW)}><Menu className="h-5 w-5" aria-hidden /></button>
        </div>
      </div>

      {/* Phone menu: white sheet from the top. Closes on overlay tap, Escape or a link. Rendered at page level (portal). */}
      {open && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <button type="button" aria-label="Close menu" className="absolute inset-0 h-full w-full cursor-default bg-[#0B1630]/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-x-3 top-3 rounded-[20px] bg-white p-4 shadow-xl">
            <div className="flex items-center justify-between">
              <Logo onDark={false} />
              <button type="button" onClick={() => setOpen(false)} aria-label="Close menu" className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0B1630]/5 text-[#0B1630]"><X className="h-5 w-5" aria-hidden /></button>
            </div>
            <nav aria-label="Main" className="mt-4 flex flex-col">
              {NAV.map((n, i) => (
                <Link key={n.to} ref={i === 0 ? firstLink : undefined} to={n.to} onClick={() => setOpen(false)} aria-current={isOn(n.to) ? "page" : undefined}
                  className={cn("flex h-12 items-center rounded-xl px-3 text-[16px] font-medium", isOn(n.to) ? "bg-tf-blue/10 text-tf-blue" : "text-[#0B1630]")}>{n.label}</Link>
              ))}
              <div className="mt-3 space-y-2">
                {isAuthenticated && home === "/team" && <Link to="/team" onClick={() => setOpen(false)} className="flex h-12 items-center justify-center rounded-full border border-[#0B1630]/15 text-[15px] font-medium text-[#0B1630]">My Team Zone</Link>}
                {isAuthenticated
                  ? <Link to="/account" onClick={() => setOpen(false)} className="flex h-12 items-center justify-center rounded-full bg-[#0B1630] text-[15px] font-medium text-white">My account</Link>
                  : <Link to="/login" onClick={() => setOpen(false)} className="flex h-12 items-center justify-center rounded-full bg-[#0B1630] text-[15px] font-medium text-white">Log in</Link>}
              </div>
            </nav>
          </div>
        </div>,
        document.body,
      )}
    </header>
  );
}
